/**
 * 归约器：`ServerFrame` / `ServerMessage` → 客户端房间状态（§5.2 / §11.3）。
 *
 * ★★ 三条互不相干的判断，谁都不许混用（§5.2）★★
 *   - `seq`   传输序号，每 session 严格 +1。**丢帧判断只看它**。
 *   - `sv`    房间状态版本。只用来给 `roomSnapshot` 择新。
 *   - `epoch` 服务进程身份。变了就整表重置。
 *
 *   这三条的判断逻辑一律走 `@poker/shared` 的 `classifyFrame` / `shouldApplySnapshot` /
 *   `cursorForNewSession`，本文件**不自己写 seq 比较**。原因是这里有两个致命陷阱：
 *   ① 按 `sv` 丢帧会吞掉全部私有消息（`error` / `actionPrompt` / `actionRejected` /
 *      `actorChanged` 都不 bump sv），乐观 UI 从此永远收不到回滚，界面会永久卡在「已下注」；
 *   ② `welcome{resumed:false}` 不调 `cursorForNewSession` 归零游标，新会话的前 N 帧
 *      会被判成 duplicate 全部丢掉，表现为「连上了但一直是空桌」。
 *
 * ★ 红线 #49：缺口只能触发**单飞 + 节流**的 `requestSnapshot`。本文件只负责喊一声
 *   `effects.requestSnapshot('gap')`，真正的单飞与节流在 `WsTransport.requestSnapshotOnce`。
 *   一旦丢一帧，之后**每条**帧都满足「不连续」，不节流就是自我 DDoS。
 *
 * ★ 重连不补播动画（§7.11 / §11.6）：快照到达时直接呈现当前状态，
 *   由 `effects.onSnapshot` 打开 resync（禁 transition 两帧），断线摘要走 `ResumeSummary`
 *   的「你错过了 N 手牌」。补播 60 个事件会变成动画风暴，且全是过期信息。
 *
 * 状态形状沿用 §11.3：**`PlayerView` 是唯一真源**，渲染模型 `TableViewModel`
 * 由渲染层 `useMemo(() => buildTableVM(view, heroSeat), [...])` 派生，不进 store。
 */
import { create } from 'zustand'
import {
  classifyFrame,
  cursorForNewSession,
  shouldApplySnapshot,
  INITIAL_FRAME_CURSOR,
  ROOM_LIFECYCLE,
  type ActionPrompt,
  type ChatEntry,
  type ErrorCode,
  type FrameCursor,
  type HandSummary,
  type LedgerEntry,
  type Millis,
  type PlayerView,
  type PublicAccount,
  type PublicHand,
  type PublicPlayer,
  type PublicSeat,
  type ResumeSummary,
  type RoomClosedReason,
  type SeatIndex,
  type ServerFrame,
  type ServerMessage,
  type ServerMessageOf,
  type ShowdownEntryView,
  type Street,
  type TopUpRequestView,
  type TransferRecord,
  type TransferRequestView,
} from '@poker/shared'

import { useTableStore, type ConnectionPhase, type SeatNote, type TableSeatInfo } from '../store/tableStore.js'

// ============================================================================
// 状态
// ============================================================================

/** welcome 带回来的会话身份。重连要用 `sessionId` + `resumeToken` 续上同一个 session。 */
export interface NetSessionInfo {
  readonly sessionId: string
  /** ★ 明文 bearer，红线 #13：绝不能进日志 */
  readonly resumeToken: string
  readonly accountId: string
  readonly account: PublicAccount
  readonly serverEpoch: string
  readonly protocolVersion: number
  readonly resumed: boolean
  /** true = 重发缓冲补不齐，等一份完整 roomSnapshot 就好 */
  readonly gap: boolean
  readonly lastProcessedCid: number
  readonly heartbeatIntervalMs: number
  readonly serverTime: Millis
}

/** 不该自动重连的终止态（§5.9）。 */
export interface NetFatal {
  readonly kind: 'superseded' | 'revoked' | 'shutdown' | 'roomClosed'
  readonly message: string
  readonly resumeAfterMs?: number
}

export interface NetError {
  readonly code: ErrorCode
  readonly message: string
  readonly retryable: boolean
  readonly retryAfterMs?: number
  /** 触发它的上行帧 cid。聊天据此认领「是我刚发的那句被拒了」，别处的提示就不必再弹一遍 */
  readonly refCid?: number
  readonly refClientActionId?: string
  readonly at: Millis
}

/** 乐观 UI 的回滚凭据：按 `clientActionId` 精确撤掉那一次，不是整体重置。 */
export interface NetRejection {
  readonly clientActionId: string
  readonly code: ErrorCode
  readonly appliedBy: 'you' | 'otherDevice' | 'timeout' | null
  readonly at: Millis
}

export interface NetState {
  readonly cursor: FrameCursor
  readonly phase: ConnectionPhase
  readonly session: NetSessionInfo | null
  /** ★ 唯一真源 */
  readonly view: PlayerView | null
  /** 私有消息，非当前行动者恒为 null */
  readonly prompt: ActionPrompt | null
  /** 本连接是否持有该 (account, room) 的行动权（多设备） */
  readonly isActor: boolean
  /**
   * 行动权最近一次**被别处拿走**的时刻；null = 没发生过或已经拿回来了。
   *
   * ★ 它只负责「解释刚才为什么点不动了」，**不是**权的真相 —— 真相一律读 `isActor`。
   *   以前这条消息会把整条连接标成 fatal + phase:'superseded'，但这条连接
   *   **根本没断**（服务端只发消息不关 socket），于是那条红条永远不会消失：
   *   fatal 只在 welcome 那一支被清，而不会再有 welcome 了。
   *   更糟的是 `WsTransport.setPhase` 的去重守卫比对的是 transport 自己的相位
   *   （那边还是 live），store 的 phase 就永久卡在 'superseded' 回不去。
   */
  readonly supersededAt: number | null
  /** 重连摘要。展示过一次后由 UI 调 `clearResume()` 清掉 */
  readonly resume: ResumeSummary | null
  readonly members: readonly PublicPlayer[]
  readonly chat: readonly ChatEntry[]
  readonly handSummaries: readonly HandSummary[]
  readonly lastError: NetError | null
  readonly lastRejection: NetRejection | null
  readonly fatal: NetFatal | null
  readonly closedRoom: { readonly reason: RoomClosedReason; readonly finalLedger: readonly LedgerEntry[] } | null
  /** 跑马：剩余要发的街，供动画层慢放 */
  readonly runItOut: readonly Street[] | null
  /** 已完成的手数。重连时作为 `ResumeRequest.lastHandNo` 上报 */
  readonly lastHandNo: number
}

export const INITIAL_NET_STATE: NetState = {
  cursor: INITIAL_FRAME_CURSOR,
  phase: 'idle',
  session: null,
  view: null,
  prompt: null,
  isActor: false,
  supersededAt: null,
  resume: null,
  members: [],
  chat: [],
  handSummaries: [],
  lastError: null,
  lastRejection: null,
  fatal: null,
  closedRoom: null,
  runItOut: null,
  lastHandNo: 0,
}

/** 与服务端 `CHAT_TAIL` 一致：本地也只留这么多，避免长局把内存吃满 */
const CHAT_TAIL = 30
const SUMMARY_TAIL = 5

// ============================================================================
// 副作用出口
// ============================================================================

export interface ApplyEffects {
  /** ★ 实现必须单飞 + 节流（红线 #49） */
  requestSnapshot?(reason: 'gap' | 'desync' | 'manual'): void
  /** 每条被应用的消息都过一遍，供 FxController 编排动画 */
  onMessage?(m: ServerMessage): void
  /** 快照落地：打开 resync（禁动画），**不要**补播动画 */
  onSnapshot?(view: PlayerView): void
  onWelcome?(w: ServerMessageOf<'welcome'>): void
  onResume?(summary: ResumeSummary): void
  onFatal?(fatal: NetFatal): void
  onError?(err: NetError): void
}

export interface ApplyContext {
  /** 本帧的 sv，写进 `PublicSeat.lastAction.atSv` */
  readonly sv: number
  readonly serverTime: Millis
}

const DEFAULT_CTX: ApplyContext = { sv: 0, serverTime: 0 }

// ============================================================================
// 小工具：对 PlayerView 做不可变补丁
// ============================================================================

function seatOf(view: PlayerView, index: SeatIndex): PublicSeat | undefined {
  return view.seats.find((s) => s.index === index)
}

/** ★ 空时服务端**不下发**这个字段（省字节），所以读的地方一律要兜 undefined。 */
function topUpsOf(view: PlayerView): readonly TopUpRequestView[] {
  return view.topUps ?? []
}

/** 在途的转账申请。与 `topUps` 一样，空时服务端不下发。 */
function transfersOf(view: PlayerView): readonly TransferRequestView[] {
  return view.transfers ?? []
}

/** 已生效的转账流水（全员可见）。空时服务端不下发。 */
function transferLogOf(view: PlayerView): readonly TransferRecord[] {
  return view.transferLog ?? []
}

/**
 * 本机保留的流水条数。与服务端 `TRANSFER_LOG_LIMIT` 同值。
 *
 * 不从服务端 import：那是另一个包。数值对不上的后果也只是本机多留/少留几行，
 * 完整历史本来就走 HTTP。
 */
const TRANSFER_LOG_TAIL = 50

function mapSeats(view: PlayerView, fn: (s: PublicSeat) => PublicSeat): PlayerView {
  return { ...view, seats: view.seats.map(fn) }
}

function patchSeat(view: PlayerView, index: SeatIndex, patch: (s: PublicSeat) => PublicSeat): PlayerView {
  return mapSeats(view, (s) => (s.index === index ? patch(s) : s))
}

/**
 * 让 `viewer.ownedSeat` 跟上一条 `seatChanged`。
 *
 * 判据只有「这个座位上坐的是不是我」：坐上去就认领，站起来（或被别人坐了）就交还。
 * 其它座位的变化一律不动 ownedSeat —— 别人坐下不该把我的视角搬走。
 */
function syncOwnedSeat(view: PlayerView, seat: PublicSeat): PlayerView {
  const me = view.viewer.accountId
  const mine = me !== null && seat.occupant?.accountId === me
  const owned = view.viewer.ownedSeat
  if (mine) {
    return owned === seat.index ? view : { ...view, viewer: { ...view.viewer, ownedSeat: seat.index } }
  }
  // 这个位置不是我坐的；如果我原本记着它，说明我刚站起来/被换走
  return owned === seat.index ? { ...view, viewer: { ...view.viewer, ownedSeat: null } } : view
}

function patchHand(view: PlayerView, handId: string, patch: (h: PublicHand) => PublicHand): PlayerView {
  const hand = view.hand
  // handId 校验先于一切（红线 #36 的客户端一侧）：上一手的迟到消息绝不能改本手
  if (hand === null || hand.handId !== handId) return view
  return { ...view, hand: patch(hand) }
}

/**
 * 底池口径与 `projectPublicHand` 逐字一致（§5.3）：`potTotal = Σ seats[].committedThisHand`。
 * 自己另算一套的话，增量流与快照会在每条街的边界上打架。
 * ★ 不能用「已收池 + 本轮下注」：翻前 pots 还是空的，而补的小盲、ante 都不算本轮下注，会被漏掉。
 */
function potTotalOf(seats: readonly PublicSeat[]): number {
  let total = 0
  for (const s of seats) total += s.committedThisHand
  return total
}

function withPotTotal(view: PlayerView): PlayerView {
  const hand = view.hand
  if (hand === null) return view
  return { ...view, hand: { ...hand, potTotal: potTotalOf(view.seats) } }
}

const HIDDEN_HOLE = { visibility: 'hidden', count: 2 } as const
const NO_HOLE = { visibility: 'none' } as const

// ============================================================================
// 单条消息
// ============================================================================

/**
 * 应用一条 `ServerMessage`。**纯函数**：不碰 store、不读时钟、不发消息。
 * 副作用一律经 `effects` 出去，测试里传空对象即可。
 */
export function applyServerMessage(
  state: NetState,
  m: ServerMessage,
  effects: ApplyEffects = {},
  ctx: ApplyContext = DEFAULT_CTX,
): NetState {
  const view = state.view

  switch (m.type) {
    // —— 连接层 ——
    case 'welcome': {
      effects.onWelcome?.(m)
      return {
        ...state,
        // ★ 新会话必须归零游标，否则新 session 的前 N 帧全被判成 duplicate（空桌 bug）
        cursor: m.resumed ? state.cursor : cursorForNewSession(m.serverEpoch),
        session: {
          sessionId: m.sessionId,
          resumeToken: m.resumeToken,
          accountId: m.accountId,
          account: m.account,
          serverEpoch: m.serverEpoch,
          protocolVersion: m.protocolVersion,
          resumed: m.resumed,
          gap: m.gap,
          lastProcessedCid: m.lastProcessedCid,
          heartbeatIntervalMs: m.heartbeatIntervalMs,
          serverTime: m.serverTime,
        },
        fatal: null,
      }
    }

    case 'pong':
      // 时钟采样在 Transport 里做（它才有 t3），这里不留状态
      return state

    case 'sessionSuperseded': {
      /**
       * ★ 不设 fatal、不动 phase。这条连接没有断，它照样在收全部广播
       *   与自己的 actionPrompt —— 说「这台设备不再更新牌局」是假话。
       *   它唯一的作用是让 UI 能解释「刚才为什么按钮没了」。
       * ★ `m.byDeviceLabel` 一律不拼进文案：房间层根本不知道对方是什么设备，
       *   把同一台机器的另一个标签页说成「另一台设备」同样是假话（服务端已改为空串）。
       * fatal 的 'superseded' 分支保留给 close(4009) 那条**真·死连接**的路。
       */
      return { ...state, supersededAt: ctx.serverTime }
    }

    case 'sessionRevoked': {
      const fatal: NetFatal = { kind: 'revoked', message: m.reason }
      effects.onFatal?.(fatal)
      return { ...state, fatal, phase: 'fatal' }
    }

    case 'serverShutdown': {
      const fatal: NetFatal = { kind: 'shutdown', message: m.reason, resumeAfterMs: m.resumeAfterMs }
      effects.onFatal?.(fatal)
      return { ...state, fatal }
    }

    case 'error': {
      const err: NetError = {
        code: m.code,
        message: m.message,
        retryable: m.retryable,
        retryAfterMs: m.retryAfterMs,
        refCid: m.refCid,
        refClientActionId: m.refClientActionId,
        at: ctx.serverTime,
      }
      effects.onError?.(err)
      return { ...state, lastError: err }
    }

    // —— 全量快照 ——
    case 'roomSnapshot': {
      // ★ sv 的唯一用途：只有快照按它择新，其余消息一律不看 sv
      if (!shouldApplySnapshot(m.sv, state.cursor.appliedSv)) return state
      const resume = m.resume ?? null
      if (resume !== null) effects.onResume?.(resume)
      effects.onSnapshot?.(m)
      return {
        ...state,
        view: m,
        prompt: m.prompt,
        isActor: m.viewer.isActor,
        supersededAt: m.viewer.isActor ? null : state.supersededAt,
        resume: resume ?? state.resume,
        chat: m.chatTail,
        handSummaries: m.handSummaryTail,
        lastHandNo: Math.max(state.lastHandNo, m.room.handNo),
        closedRoom: null,
        runItOut: null,
        lastRejection: null,
      }
    }

    // —— 房间增量 ——
    case 'roomConfigChanged': {
      if (view === null) return state
      return {
        ...state,
        view:
          m.appliesAt === 'now'
            ? { ...view, room: { ...view.room, config: m.config, pendingConfig: null } }
            : { ...view, room: { ...view.room, pendingConfig: m.config } },
      }
    }

    case 'roomPhaseChanged':
      if (view === null) return state
      return { ...state, view: { ...view, room: { ...view.room, phase: m.phase } } }

    case 'hostChanged': {
      if (view === null) return state
      const withHost = mapSeats(view, (s) =>
        s.occupant === null
          ? s
          : { ...s, occupant: { ...s.occupant, isHost: s.occupant.accountId === m.hostAccountId } },
      )
      return {
        ...state,
        view: { ...withHost, room: { ...withHost.room, hostAccountId: m.hostAccountId } },
        members: state.members.map((p) => ({ ...p, isHost: p.accountId === m.hostAccountId })),
      }
    }

    case 'playerJoined': {
      const rest = state.members.filter((p) => p.accountId !== m.player.accountId)
      return { ...state, members: [...rest, m.player] }
    }

    case 'playerLeft': {
      const members = state.members.filter((p) => p.accountId !== m.accountId)
      if (view === null) return { ...state, members }
      // 座位通常还会有一条 seatChanged，这里顺手清一次，避免中间态显示「幽灵玩家」
      const cleared = mapSeats(view, (s) =>
        s.occupant?.accountId === m.accountId ? { ...s, occupant: null, presence: 'empty' } : s,
      )
      // 离开的是自己（多设备被踢 / 自己站起）：视角也要交还，否则牌桌还按旧座位转
      const leftSelf = m.accountId === view.viewer.accountId
      return {
        ...state,
        members,
        view: leftSelf ? { ...cleared, viewer: { ...cleared.viewer, ownedSeat: null } } : cleared,
      }
    }

    case 'seatChanged': {
      if (view === null) return state
      // ★ 同步 viewer.ownedSeat。协议只在 roomSnapshot 里给 ownedSeat，
      //   而「坐下 / 站起」只会来一条 seatChanged —— 不在这里跟着改的话，
      //   自己坐下之后 `ownedSeat` 会一直停在 null：牌桌不会把自己转到下方、
      //   名单上没有「（你）」、`heroSeat` 恒 null 连红线 #50 的快进都不触发，
      //   直到下一份快照到达才恢复。
      return { ...state, view: syncOwnedSeat(patchSeat(view, m.seat.index, () => m.seat), m.seat) }
    }

    case 'roomClosed': {
      const fatal: NetFatal = { kind: 'roomClosed', message: m.reason }
      effects.onFatal?.(fatal)
      return {
        ...state,
        closedRoom: { reason: m.reason, finalLedger: m.finalLedger },
        fatal,
        view: view === null ? null : { ...view, room: { ...view.room, phase: 'closed' } },
      }
    }

    case 'actorChanged':
      // 拿回行动权 ⇒ 「被别处接管」这件事已经过去了，痕迹跟着清掉
      return { ...state, isActor: m.isActor, supersededAt: m.isActor ? null : state.supersededAt }

    // —— 买分与结算 ——
    //
    // ★ 这三条**必须**在这里落进 `view`：`topUps` / `settlement` 只在 `roomSnapshot`
    //   里有全量，而买分的整段交互（申请 → 等批准 → 本手后到账）没有任何一步会触发快照。
    //   不归约的话，玩家点完申请界面毫无变化，房主也永远看不到队列，
    //   直到下一次 gap 触发快照才突然冒出来。
    case 'topUpRequested': {
      if (view === null) return state
      const seat = seatOf(view, m.seat)
      const occupant = seat?.occupant ?? null
      // 座位是空的（迟到帧 / 人已离座）：这笔申请没有归属，渲染出来也只是一行幽灵
      if (occupant === null) return state
      const req: TopUpRequestView = {
        requestId: m.requestId,
        seat: m.seat,
        accountId: occupant.accountId,
        nickname: occupant.nickname,
        status: 'pending',
        // ★ 显示用的**预估**额。真正到账额由服务端在生效那一刻重算（红线 #38）
        amount: m.amount,
        requestedAt: m.requestedAt,
        expiresAt: m.requestedAt + ROOM_LIFECYCLE.TOPUP_REQUEST_TTL_MS,
      }
      // 同一座位只留一笔：服务端「替换旧申请」会先发一条 cancelled，
      // 这里按 seat 再滤一次，万一那条没到也不会留下一行点不动的幽灵申请
      const rest = topUpsOf(view).filter((r) => r.requestId !== m.requestId && r.seat !== m.seat)
      return { ...state, view: { ...view, topUps: [...rest, req] } }
    }

    case 'topUpResolved': {
      if (view === null) return state
      const list = topUpsOf(view)
      // 无 appliedAt 的 approved = 房主批了，但手牌进行中，要等手牌边界才到账（红线 #41）。
      // 这一态必须留在列表里，否则申请人点完批准看不到任何变化，只会反复再申请。
      if (m.outcome === 'approved' && m.appliedAt === undefined) {
        return {
          ...state,
          view: {
            ...view,
            topUps: list.map((r) =>
              r.requestId === m.requestId ? { ...r, status: 'approved', expiresAt: null } : r,
            ),
          },
        }
      }
      // 其余（已到账 / 拒绝 / 撤回 / 超时）都是终局：出队。
      // 筹码的变化由同一 step 里的 seatChanged 负责，这里不碰任何座位。
      return { ...state, view: { ...view, topUps: list.filter((r) => r.requestId !== m.requestId) } }
    }

    case 'gameSettled': {
      if (view === null) return state
      // 结算后房间会被 closeRoom 关掉，战报只能从这里（或快照）拿到，必须存住。
      // 上一手的确认窗口和在途的买分 / 转账申请一并清空：
      // 整局战报已经接管界面，留着只会造成重复信息或一排点不动的行。
      return {
        ...state,
        view: { ...view, readyState: undefined, settlement: m.report, topUps: [], transfers: [] },
      }
    }

    // —— 玩家间转账 ——
    //
    // 与买分同构，且理由一样：整段交互（求购 → 等批准 → 手牌边界划账）没有任何一步
    // 会触发快照，不在这里归约的话，申请人点完什么都没变、卖家永远看不到求购。
    //
    // ★ 方向只记一句话：**fromSeat 向 toSeat 买 amount 筹码**。
    //   fromSeat 是买家（收筹码、发起人），toSeat 是卖家（出筹码、批准人）。
    case 'transferRequested': {
      if (view === null) return state
      const buyer = seatOf(view, m.fromSeat)?.occupant ?? null
      const seller = seatOf(view, m.toSeat)?.occupant ?? null
      // 任一端的座位是空的（迟到帧 / 人已离座）：这笔申请没有归属，渲染出来只是一行幽灵
      if (buyer === null || seller === null) return state
      const req: TransferRequestView = {
        requestId: m.requestId,
        fromSeat: m.fromSeat,
        toSeat: m.toSeat,
        fromAccountId: buyer.accountId,
        toAccountId: seller.accountId,
        fromNickname: buyer.nickname,
        toNickname: seller.nickname,
        status: 'pending',
        // ★ 显示用的**上限**。真正转走多少由服务端在生效那一刻重算（红线 #38）
        amount: m.amount,
        requestedAt: m.requestedAt,
        expiresAt: m.requestedAt + ROOM_LIFECYCLE.TOPUP_REQUEST_TTL_MS,
      }
      // 同一对 (买家→卖家) 只留一笔：服务端「替换旧申请」会先发一条 cancelled，
      // 这里按这一对再滤一次，万一那条没到也不会留下一行点不动的幽灵申请
      const rest = transfersOf(view).filter(
        (r) => r.requestId !== m.requestId && !(r.fromSeat === m.fromSeat && r.toSeat === m.toSeat),
      )
      return { ...state, view: { ...view, transfers: [...rest, req] } }
    }

    case 'transferResolved': {
      if (view === null) return state
      const list = transfersOf(view)
      // 无 appliedAt 的 approved = 卖家批了，但手牌进行中，要等手牌边界才划账（红线 #41）。
      // 这一态必须留在列表里，否则双方点完批准看不到任何变化，只会以为没生效。
      if (m.outcome === 'approved' && m.appliedAt === undefined) {
        return {
          ...state,
          view: {
            ...view,
            transfers: list.map((r) =>
              r.requestId === m.requestId ? { ...r, status: 'approved', expiresAt: null } : r,
            ),
          },
        }
      }
      // 其余（已划账 / 拒绝 / 撤回 / 超时）都是终局：出队。
      // 筹码的变化由同一 step 里的两条 seatChanged 负责，这里不碰任何座位。
      const transfers = list.filter((r) => r.requestId !== m.requestId)
      // ★ 流水只在真正划账时增长，而且**全员可见**（防 chip dumping 的唯一手段）
      const record = m.record
      const log =
        record === undefined
          ? transferLogOf(view)
          : [...transferLogOf(view).filter((r) => r.requestId !== record.requestId), record].slice(
              -TRANSFER_LOG_TAIL,
            )
      return { ...state, view: { ...view, transfers, transferLog: log } }
    }

    // —— 开下一局的全员确认 ——
    //
    // 关闭通常**没有单独的帧**：下一手开起来（`handStarted`）或手牌作废（`handVoided`）
    // 就是关闭信号，两处各自把 `readyState` 清掉，否则结算浮层会一直挂在新的一手上。
    //
    // ★ `state: null` 是第三条关闭路径：窗口关了却**没有下一手**（最后一个人确认时
    //   房主正暂停着 / 对手刚破产）。那时上面两条帧一条都不会来，不收起浮层的话，
    //   它就永远盖在桌面上，底下的补分、离座全点不到。
    case 'readyStateChanged': {
      if (view === null) return state
      if (m.state === null) return { ...state, view: { ...view, readyState: undefined } }
      return { ...state, view: { ...view, readyState: m.state } }
    }

    // —— 牌局增量 ——
    case 'handStarted': {
      if (view === null) return state
      const started: PublicHand = {
        handId: m.handId,
        handNo: m.handNo,
        phase: 'dealing',
        street: null,
        board: [],
        pots: [],
        potTotal: 0,
        buttonSeat: m.buttonSeat,
        sbSeat: m.sbSeat,
        bbSeat: m.bbSeat,
        deadButton: m.deadButton,
        betToMatch: 0,
        // 红线 #23：每条街重置为 bigBlind，开局也一样
        lastFullRaiseSize: m.bb,
        lastAggressorSeatThisStreet: null,
        actorSeat: null,
        actionNo: 0,
        actorSince: null,
        displayDeadlineAt: null,
        hardDeadlineAt: null,
        actionLog: [],
        uncalledReturn: null,
        showdown: [],
        result: null,
        startedAt: ctx.serverTime,
      }
      const inHand = new Map(m.seats.map((s) => [s.seat, s]))
      const seats = view.seats.map((s): PublicSeat => {
        const p = inHand.get(s.index)
        return {
          ...s,
          inHand: p !== undefined,
          handState: 'live',
          committedThisRound: 0,
          committedThisHand: 0,
          stack: p?.stack ?? s.stack,
          holeCards: p === undefined ? NO_HOLE : HIDDEN_HOLE,
          lastAction: null,
        }
      })
      return {
        ...state,
        view: {
          ...view,
          seats,
          hand: started,
          room: { ...view.room, handNo: m.handNo },
          // 确认窗口随新的一手关闭；服务端不为此单发一帧，收到这条即收起结算浮层
          readyState: undefined,
        },
        prompt: null,
        runItOut: null,
        lastRejection: null,
      }
    }

    case 'blindsPosted': {
      if (view === null) return state
      let next = view
      for (const post of m.posts) {
        next = patchSeat(next, post.seat, (s) => ({
          ...s,
          stack: Math.max(0, s.stack - post.amount),
          // ★ ante 与补的小盲（dead）不计入本轮投入（引擎口径：committedThisRound 只含活的盲注与下注），
          //   算进去的话 preflop 的 betToMatch 会被抬高，跟注额全错
          committedThisRound:
            post.kind === 'ante' || post.kind === 'dead' ? s.committedThisRound : s.committedThisRound + post.amount,
          committedThisHand: s.committedThisHand + post.amount,
          handState: post.allIn ? 'allIn' : s.handState,
        }))
      }
      // 新服务端连强制投入的动作流水一起下发：追加进 actionLog，气泡（「补盲 30」）与快照同步出现。
      // 按 index 去重：重连补发或与快照交错时不会重复
      const actions = m.actions ?? []
      for (const a of actions) {
        next = patchSeat(next, a.seat, (s) => ({ ...s, lastAction: { kind: a.type, to: a.to, atSv: ctx.sv } }))
      }
      const bet = next.seats.reduce((max, s) => Math.max(max, s.committedThisRound), 0)
      next = patchHand(next, m.handId, (h) => {
        const fresh = actions.filter((a) => a.index >= h.actionLog.length)
        const last = fresh[fresh.length - 1]
        return {
          ...h,
          betToMatch: Math.max(h.betToMatch, bet),
          phase: 'preflop',
          street: 'preflop',
          ...(fresh.length > 0 ? { actionLog: [...h.actionLog, ...fresh] } : {}),
          ...(last !== undefined ? { actionNo: Math.max(h.actionNo, last.index + 1) } : {}),
        }
      })
      return { ...state, view: withPotTotal(next) }
    }

    case 'holeCardsDealt': {
      if (view === null) return state
      let next = view
      for (const entry of m.seats) {
        next = patchSeat(next, entry.seat, (s) => ({ ...s, holeCards: entry.holeCards }))
      }
      return { ...state, view: patchHand(next, m.handId, (h) => ({ ...h, phase: h.phase === 'dealing' ? 'preflop' : h.phase })) }
    }

    case 'turnChanged': {
      if (view === null) return state
      return {
        ...state,
        view: patchHand(view, m.handId, (h) => ({
          ...h,
          actorSeat: m.seat,
          actionNo: m.actionNo,
          actorSince: m.promptedAt,
          displayDeadlineAt: m.displayDeadlineAt,
          hardDeadlineAt: m.hardDeadlineAt,
        })),
      }
    }

    case 'actionPrompt': {
      const p = m.prompt
      const nextView =
        view === null
          ? null
          : patchHand(view, p.handId, (h) => ({
              ...h,
              actorSeat: p.seat,
              actionNo: p.actionNo,
              displayDeadlineAt: p.displayDeadlineAt,
              hardDeadlineAt: p.hardDeadlineAt,
            }))
      return { ...state, prompt: p, view: nextView, lastRejection: null }
    }

    case 'actionTimerExtended': {
      if (view === null) return state
      const next = patchHand(view, m.handId, (h) =>
        h.actionNo !== m.actionNo
          ? h
          : { ...h, displayDeadlineAt: m.displayDeadlineAt, hardDeadlineAt: m.hardDeadlineAt },
      )
      const prompt =
        state.prompt !== null && state.prompt.handId === m.handId && state.prompt.actionNo === m.actionNo
          ? {
              ...state.prompt,
              displayDeadlineAt: m.displayDeadlineAt,
              hardDeadlineAt: m.hardDeadlineAt,
              timeBankMsRemaining: m.timeBankMsRemaining,
            }
          : state.prompt
      return { ...state, view: next, prompt }
    }

    case 'playerActed': {
      if (view === null) return state
      const a = m.action
      const seatBefore = seatOf(view, a.seat)
      const wasFolded = a.type === 'fold'
      let next = patchSeat(view, a.seat, (s) => ({
        ...s,
        stack: m.stackAfter,
        // `to` 恒为动作后的 committedThisRound（raise-to 语义，红线 #18），直接赋值
        committedThisRound: a.to,
        committedThisHand: s.committedThisHand + a.delta,
        handState: wasFolded ? 'folded' : a.allIn ? 'allIn' : s.handState,
        lastAction: { kind: a.type, to: a.to, atSv: ctx.sv },
      }))
      next = patchHand(next, m.handId, (h) => ({
        ...h,
        actionLog: [...h.actionLog, a],
        actionNo: a.index + 1,
        betToMatch: Math.max(h.betToMatch, a.to),
        lastAggressorSeatThisStreet:
          a.type === 'bet' || a.type === 'raise' ? a.seat : h.lastAggressorSeatThisStreet,
        // 行动权交还给「未知」：下一条 turnChanged 会给出真正的行动者
        actorSeat: null,
        actorSince: null,
        displayDeadlineAt: null,
        hardDeadlineAt: null,
      }))
      // 自己的动作被确认 → 本地 prompt 作废（回滚靠 actionRejected，不靠这里）
      const prompt = seatBefore !== undefined && state.prompt?.seat === a.seat ? null : state.prompt
      return { ...state, view: withPotTotal(next), prompt }
    }

    case 'actionRejected': {
      const rejection: NetRejection = {
        clientActionId: m.clientActionId,
        code: m.code,
        appliedBy: m.appliedBy,
        at: ctx.serverTime,
      }
      // prompt 非 null = 行动权还在自己手上，直接用它重建动作区；
      // 为 null 则说明这一步已经被别人/超时执行掉了，必须拿一份快照才敢继续画
      if (m.prompt === null) effects.requestSnapshot?.('desync')
      return { ...state, lastRejection: rejection, prompt: m.prompt }
    }

    case 'communityCards': {
      if (view === null) return state
      // 街末的收池已经由 potsUpdated 落地，这里把本街投入清零（引擎 openBettingRound 的镜像）
      const cleared = mapSeats(view, (s) => (s.committedThisRound === 0 ? s : { ...s, committedThisRound: 0 }))
      const bb = cleared.room.config.bigBlind
      const next = patchHand(cleared, m.handId, (h) => ({
        ...h,
        board: [...h.board, ...m.cards],
        street: m.street,
        phase: m.street,
        betToMatch: 0,
        lastFullRaiseSize: bb,
        lastAggressorSeatThisStreet: null,
      }))
      return { ...state, view: withPotTotal(next) }
    }

    case 'uncalledBetReturned': {
      if (view === null) return state
      const next = patchSeat(view, m.seat, (s) => ({
        ...s,
        stack: m.stackAfter,
        committedThisRound: Math.max(0, s.committedThisRound - m.amount),
        committedThisHand: Math.max(0, s.committedThisHand - m.amount),
      }))
      return {
        ...state,
        view: withPotTotal(
          patchHand(next, m.handId, (h) => ({ ...h, uncalledReturn: { seat: m.seat, amount: m.amount } })),
        ),
      }
    }

    case 'potsUpdated': {
      if (view === null) return state
      // 收池瞬间用服务端给的 potTotal（与 Σ committedThisHand 相等，这里直接采纳权威值）
      return {
        ...state,
        view: patchHand(view, m.handId, (h) => ({ ...h, pots: m.pots, potTotal: m.potTotal })),
      }
    }

    case 'runItOut':
      return { ...state, runItOut: m.remainingStreets }

    case 'cardsRevealed': {
      if (view === null) return state
      const shown = { visibility: 'shown', cards: m.cards, hand: m.hand } as const
      const next = patchSeat(view, m.seat, (s) => ({ ...s, holeCards: shown }))
      return {
        ...state,
        view: patchHand(next, m.handId, (h) => {
          const entry: ShowdownEntryView = {
            seat: m.seat,
            revealOrder: h.showdown.length,
            mucked: false,
            cards: m.cards,
            hand: m.hand,
          }
          return { ...h, showdown: [...h.showdown.filter((e) => e.seat !== m.seat), entry] }
        }),
      }
    }

    case 'cardsMucked': {
      if (view === null) return state
      // ★ 红线 #3：盖牌就是盖牌，本地也不留任何牌面痕迹
      const next = patchSeat(view, m.seat, (s) => ({ ...s, holeCards: { visibility: 'mucked' } }))
      return {
        ...state,
        view: patchHand(next, m.handId, (h) => {
          const entry: ShowdownEntryView = { seat: m.seat, revealOrder: h.showdown.length, mucked: true }
          return { ...h, showdown: [...h.showdown.filter((e) => e.seat !== m.seat), entry] }
        }),
      }
    }

    case 'handEnded': {
      if (view === null) return state
      const stacks = new Map(m.result.seats.map((s) => [s.seat, s.endingStack]))
      const seats = view.seats.map((s): PublicSeat => {
        const ending = stacks.get(s.index)
        return {
          ...s,
          stack: ending ?? s.stack,
          committedThisRound: 0,
          inHand: false,
        }
      })
      const hand = view.hand
      const ended: PublicHand | null =
        hand === null || hand.handId !== m.handId
          ? hand
          : {
              ...hand,
              phase: 'complete',
              board: m.result.board,
              pots: m.result.pots,
              potTotal: m.result.pots.reduce((sum, p) => sum + p.amount, 0),
              showdown: m.result.showdown,
              result: m.result,
              actorSeat: null,
              actorSince: null,
              displayDeadlineAt: null,
              hardDeadlineAt: null,
            }
      const summaries = [...state.handSummaries, m.summary].slice(-SUMMARY_TAIL)
      return {
        ...state,
        view: { ...view, seats, hand: ended },
        prompt: null,
        runItOut: null,
        handSummaries: summaries,
        lastHandNo: Math.max(state.lastHandNo, m.result.handNo),
      }
    }

    case 'handVoided': {
      if (view === null) return state
      // 红线 #25：作废手牌不推进按钮指针，服务端回传恢复后的 buttonSeat
      const seats = view.seats.map((s): PublicSeat => ({
        ...s,
        inHand: false,
        committedThisRound: 0,
        committedThisHand: 0,
        holeCards: NO_HOLE,
        lastAction: null,
      }))
      return {
        ...state,
        // 作废的手牌没有结算浮层可看，确认窗口也不会开（服务端走固定短延迟）
        view: { ...view, seats, hand: null, readyState: undefined },
        prompt: null,
        runItOut: null,
      }
    }

    // —— 社交 ——
    case 'chat':
      return { ...state, chat: [...state.chat, m.entry].slice(-CHAT_TAIL) }

    case 'emote':
      // 纯动效，不留状态（由 effects.onMessage 交给 FxController）
      return state

    default:
      return state
  }
}

// ============================================================================
// 整帧
// ============================================================================

export interface FrameOutcome {
  readonly state: NetState
  /** classifyFrame 的判定，调试与埋点用 */
  readonly verdict: 'apply' | 'duplicate' | 'gap'
  readonly reset: boolean
}

/**
 * 应用一帧。顺序严格按 §5.2：
 *   epoch 变化 → 整表重置 → welcome 跳过连续性检查 → 去重 → 缺口 → 逐条应用。
 *
 * ★ 缺口时**不应用本帧**，只请求一次快照。硬要应用会把状态推到一个既不是
 *   「缺口前」也不是「缺口后」的中间态，之后所有增量都建立在错误的基线上。
 */
export function applyServerFrame(state: NetState, frame: ServerFrame, effects: ApplyEffects = {}): FrameOutcome {
  const decision = classifyFrame(state.cursor, frame)

  if (decision.verdict === 'duplicate') {
    return { state, verdict: 'duplicate', reset: false }
  }
  if (decision.verdict === 'gap') {
    // ★ 红线 #49：这一声必须落在单飞 + 节流的实现上
    effects.requestSnapshot?.('gap')
    return { state, verdict: 'gap', reset: false }
  }

  // 快照择新要用**本帧之前**的 appliedSv（decision.next 里的已经是 max 过的）
  const appliedSvBefore = state.cursor.appliedSv
  let next: NetState = decision.reset
    ? {
        ...INITIAL_NET_STATE,
        // 服务端换进程：房间状态全作废（在途手牌已按红线 #42 被 void），
        // 但连接相位与会话身份留给 Transport 处理
        cursor: decision.next,
        phase: state.phase,
        session: state.session,
      }
    : { ...state, cursor: decision.next }

  const ctx: ApplyContext = { sv: frame.sv, serverTime: frame.st }
  for (const msg of frame.msgs) {
    if (msg.type === 'roomSnapshot' && !shouldApplySnapshot(msg.sv, appliedSvBefore)) continue
    next = applyServerMessage(next, msg, effects, ctx)
    effects.onMessage?.(msg)
  }
  return { state: next, verdict: 'apply', reset: decision.reset }
}

// ============================================================================
// store 绑定
// ============================================================================

export interface NetStore extends NetState {
  ingestFrame(frame: ServerFrame, effects?: ApplyEffects): FrameOutcome
  ingestMessage(m: ServerMessage, effects?: ApplyEffects, ctx?: ApplyContext): void
  setPhase(phase: ConnectionPhase): void
  clearResume(): void
  clearRejection(): void
  clearError(): void
  /**
   * 本机离开了房间（发完 `leaveRoom` 之后调）。
   *
   * ★ 只清**房间范围**的字段，`cursor` / `phase` / `session` 一概不动 ——
   *   连接还是同一条，服务端的 `seq` 还在往上涨。用 `reset()` 代替它会把
   *   `appliedSeq` 归零，而这条会话的下一帧 seq 是 121，`classifyFrame` 从此
   *   每帧判 gap，整条连接永久失聪（换房间时表现为「进去了却一直空桌」）。
   *
   * ★ 为什么需要这个动作：离房后服务端**不会**再给这条连接推任何东西，
   *   也没有一条「你已离开」的消息，本机那份 `view` 只能自己清。
   */
  leaveRoom(): void
  reset(): void
}

/**
 * 联机模式的权威 store。`view` 是 `PlayerView`（§11.3 的「唯一真源」），
 * 渲染模型在渲染层派生，不进这里。
 */
export const useNetStore = create<NetStore>((set, get) => ({
  ...INITIAL_NET_STATE,

  ingestFrame(frame, effects = {}) {
    const outcome = applyServerFrame(get(), frame, effects)
    if (outcome.verdict === 'apply') set(outcome.state)
    return outcome
  },

  ingestMessage(m, effects = {}, ctx = DEFAULT_CTX) {
    set(applyServerMessage(get(), m, effects, ctx))
  },

  setPhase: (phase) => set({ phase }),
  clearResume: () => set({ resume: null }),
  clearRejection: () => set({ lastRejection: null }),
  clearError: () => set({ lastError: null }),
  leaveRoom: () =>
    set((s) => ({
      // ★ `sv` 是**房间**状态版本，不是连接版本：换房间要归零。
      //   不归零的话，旧房间的 sv（比如 76）会把新房间那份 sv=1 的首帧快照
      //   当成「过期快照」丢掉 —— 症状是「进去了但一直空桌」，而且永远不自愈，
      //   因为后续增量也都建立在这份没落地的快照上。
      //   `appliedSeq` 相反，它是连接维度的，必须原样留着（见下面的注释）。
      cursor: { ...s.cursor, appliedSv: 0 },
      view: null,
      prompt: null,
      isActor: false,
      resume: null,
      members: [],
      chat: [],
      handSummaries: [],
      lastError: null,
      lastRejection: null,
      fatal: null,
      closedRoom: null,
      runItOut: null,
      lastHandNo: 0,
    })),
  reset: () => set({ ...INITIAL_NET_STATE }),
}))

// ============================================================================
// 精确订阅切片
// ============================================================================

export const selectNetView = (s: NetState): PlayerView | null => s.view
export const selectPrompt = (s: NetState): ActionPrompt | null => s.prompt
/** 名字带 Net 前缀，免得和 `store/tableStore` 的同名切片在 import 时撞车 */
export const selectNetHeroSeat = (s: NetState): SeatIndex | null => s.view?.viewer.ownedSeat ?? null
export const selectNetPhase = (s: NetState): ConnectionPhase => s.phase
export const selectIsActor = (s: NetState): boolean => s.isActor
export const selectRoomCode = (s: NetState): string | null => s.view?.room.code ?? null
export const selectResume = (s: NetState): ResumeSummary | null => s.resume
/** 能否加注**只看** `canRaise`（红线 #21），绝不用 minRaiseTo < maxRaiseTo 推断 */
export const selectCanRaise = (s: NetState): boolean => s.prompt?.canRaise ?? false

/**
 * 座位元信息投影：`PublicSeat` → 现有 UI 组件吃的 `TableSeatInfo`。
 * 空座位不进列表（`tableStore` 靠座位集合决定排布）。
 */
export function toTableSeats(view: PlayerView | null): TableSeatInfo[] {
  if (view === null) return []
  const out: TableSeatInfo[] = []
  const topUps = topUpsOf(view)
  for (const s of view.seats) {
    if (s.occupant === null) continue
    out.push({
      seat: s.index,
      name: s.occupant.nickname,
      stack: s.stack,
      isBot: s.occupant.isBot,
      note: seatNoteOf(s, topUps),
      avatarSeed: s.occupant.avatarSeed,
      accountId: s.occupant.accountId,
    })
  }
  return out
}

/**
 * 座位角标该说哪件事（见 `SeatNote`）。**顺序即优先级**：
 *   1. 只有真的断线才算离线：坐出的人人还在，座位上该显示「暂离」而不是「离线」；
 *   2. 筹码为 0 优先于暂离：输光的人服务端会顺手把他置成暂离，可他缺的是筹码，
 *      不是「回到牌局」—— 有一笔买分在途（待批 / 已批待到账）就说「买分中」；
 *   3. 暂离（系统判的、自己点的、这手打完就暂离的）。
 * 本手还拿着牌的人（全下时筹码也是 0）由座位组件自己不画这枚角标，这里不管手牌。
 */
export function seatNoteOf(s: PublicSeat, topUps: readonly TopUpRequestView[]): SeatNote | null {
  const occupant = s.occupant
  if (occupant === null) return null
  if (s.presence === 'disconnected' || s.presence === 'idleTimeout') return 'offline'
  if (s.stack <= 0) {
    const buying = topUps.some((r) => r.seat === s.index && r.accountId === occupant.accountId)
    return buying ? 'buying' : 'broke'
  }
  if (s.presence === 'sittingOut' || s.presence === 'awayUser' || s.sitOutNextHand) return 'away'
  return null
}

/**
 * 把联机状态镜像进 `tableStore` 的壳层字段（房间码 / 配置 / 座位 / hero / 连接相位）。
 *
 * ★ 刻意**不写** `tableStore.view`：那个字段是引擎的 `PublicHandState`（本地模式用），
 *   联机模式的真源是 `PlayerView`，两者不同构。硬塞会造出第二份「可能不一致的真相」。
 *   联机牌桌请从 `useNetStore.view` 派生渲染模型。详见 deviations。
 */
function sameSeats(a: readonly TableSeatInfo[], b: readonly TableSeatInfo[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (x === undefined || y === undefined) return false
    if (
      x.seat !== y.seat ||
      x.name !== y.name ||
      x.stack !== y.stack ||
      x.isBot !== y.isBot ||
      x.note !== y.note ||
      x.avatarSeed !== y.avatarSeed ||
      x.accountId !== y.accountId
    ) {
      return false
    }
  }
  return true
}

/** 上次镜像过去的值。★ 必须做这层 diff：牌局高峰期每秒几十帧，无脑 setSeats 会让整排座位每帧重渲染 */
let mirrored: { seats: readonly TableSeatInfo[]; code: string | null; config: unknown; hero: SeatIndex | null } = {
  seats: [],
  code: null,
  config: null,
  hero: null,
}

export function mirrorToTableStore(state: NetState): void {
  const table = useTableStore.getState()
  const view = state.view
  table.setConnection(state.phase)

  const code = view?.room.code ?? null
  const config = view?.room.config ?? null
  if (code !== mirrored.code || config !== mirrored.config) {
    table.setRoom(code, config)
    mirrored = { ...mirrored, code, config }
  }

  const seats = toTableSeats(view)
  if (!sameSeats(seats, mirrored.seats)) {
    table.setSeats(seats)
    mirrored = { ...mirrored, seats }
  }

  const hero = view?.viewer.ownedSeat ?? null
  if (hero !== mirrored.hero) {
    table.setHeroSeat(hero)
    mirrored = { ...mirrored, hero }
  }
}

/** 订阅 net → table 的镜像。返回取消订阅函数。 */
export function startTableMirror(): () => void {
  // 换会话时 diff 基线必须清掉，否则新房间的第一份座位会被当成「没变」而不下发
  mirrored = { seats: [], code: null, config: null, hero: null }
  mirrorToTableStore(useNetStore.getState())
  return useNetStore.subscribe((s) => {
    mirrorToTableStore(s)
  })
}
